import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { Plus } from "lucide-react";

interface ClientInfo {
  _key: string;
  name: string;
  icon: string;
  description: string;
  points: number;
  recent_event: { type: string; points: number; created_at: string } | null;
}

export function ClientList() {
  const { jwt } = useParams();
  const navigate = useNavigate();
  const { data: clients, loading, error } = useApi<ClientInfo[]>(
    `/api/admin/${jwt}/clients`,
  );

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-app-text">Partners</h1>
          <p className="text-sm text-app-text-muted mt-0.5">Manage your partners</p>
        </div>
        <Button
          onClick={() => navigate(`/admin/${jwt}/clients/new`)}
          className="bg-linear-to-r from-app-primary to-app-accent hover:opacity-90 transition-opacity shadow-md text-white font-medium px-5"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add Partner
        </Button>
      </div>

      {!clients || clients.length === 0 ? (
        <EmptyState
          icon="👥"
          title="No partners yet"
          description="Add your first partner to get started!"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          {clients.map((client) => (
            <Card
              key={client._key}
              className="cursor-pointer border-0 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.01] overflow-hidden py-0"
              onClick={() => navigate(`/admin/${jwt}/${client._key}`)}
            >
              <CardContent className="p-0">
                {/* Card gradient accent strip */}
                <div className="h-1.5 bg-linear-to-r from-app-primary via-app-accent to-app-success" />
                <div className="p-3 md:p-4">
                  <div className="flex items-start gap-3">
                    {/* Emoji in colored circle */}
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-app-primary/10 flex items-center justify-center shadow-sm shrink-0">
                      <span className="text-2xl md:text-3xl">{client.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-base font-semibold text-app-text truncate">{client.name}</h3>
                        {/* Points badge */}
                        <div className={`shrink-0 px-2.5 py-0.5 rounded-full text-sm font-bold ${client.points >= 0 ? "bg-app-success/15 text-app-success" : "bg-app-danger/15 text-app-danger"}`}>
                          {client.points >= 0 ? "+" : ""}{client.points}
                        </div>
                      </div>
                      {client.description && (
                        <MarkdownText text={client.description} className="text-sm text-app-text-muted truncate mt-0.5" plain />
                      )}
                      {client.recent_event && (
                        <div className="mt-2 px-2 py-1 rounded-md bg-app-surface-raised text-xs text-app-text-muted truncate">
                          {({chore_completed:"✅",chore_overdue:"⏰",rule_broken:"⚠️",rule_streak:"🔥",punishment_redeemed:"💀",reward_redeemed:"🎁",punishment_reversed:"↩️",reward_reversed:"↩️"}[client.recent_event.type]||"📝")} {client.recent_event.type?.replace(/_/g," ")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
